import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import { Logo } from "../components/Logo";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import {
  ArrowRight,
  ArrowUpRight,
  Cpu,
  Bot,
  LineChart,
  Zap,
  Smartphone,
  Database,
  ShoppingBag,
  Users,
  Code2,
  Megaphone,
  ShieldCheck,
  Layers,
  Sparkles,
  ScanLine,
  Building2,
  MapPin,
  Check,
  Mail,
  Phone as PhoneIcon,
  ExternalLink,
} from "lucide-react";

/* -----------------------------------------------------------------------------
 * Brand & product configuration
 * External product URLs are kept here so they can be updated in one place.
 * When a URL is not yet known, we keep it empty and the button renders
 * disabled — we deliberately do NOT navigate to a fake URL.
 * -------------------------------------------------------------------------- */
const GETUP_URL = "https://www.getupsolutions.com.au";
const CONTACT = Object.freeze({
  legalName: "Getup Solutions Pty Ltd",
  abn: "73 609 022 451",
  addressLines: ["214/757 Bourke Street", "Docklands VIC 3008", "Australia"],
  email: "accounts@getupsolutions.com.au",
  tollFree: "1800 951 438",
  mobile: "0483 934 935",
  website: GETUP_URL,
});

const SERVICE_CATEGORIES = [
  {
    key: "ai-automation-crm",
    label: "AI, Automation & CRM",
    items: [
      {
        icon: Cpu,
        title: "AI Digital Transformation",
        body: "Strategy, audit and roadmap.",
      },
      {
        icon: Bot,
        title: "AI Automation",
        body: "Replace manual workflows with AI.",
      },
      {
        icon: LineChart,
        title: "CRM Automation",
        body: "Pipelines + AI lead qualification.",
      },
    ],
  },
  {
    key: "software-apps-systems",
    label: "Software, Apps & Systems",
    items: [
      {
        icon: Zap,
        title: "MVP Software Development",
        body: "Production AI MVPs.",
      },
      {
        icon: Smartphone,
        title: "Mobile App Development",
        body: "iOS + Android with AI built-in.",
      },
      {
        icon: Database,
        title: "ERP / Custom Business Systems",
        body: "Scoped after audit.",
      },
    ],
  },
  {
    key: "growth-commerce",
    label: "Growth & Commerce",
    items: [
      {
        icon: LineChart,
        title: "Digital Growth",
        body: "Web, SEO & paid acquisition.",
      },
      {
        icon: ShoppingBag,
        title: "E-commerce",
        body: "Shopify, BigCommerce & custom.",
      },
    ],
  },
];

const PRODUCTS = [
  {
    key: "getup-ai-crm",
    name: "Getup AI CRM",
    category: "Intelligent CRM and business communication",
    logo: "/brands/getup-ai-crm-logo.svg",
    logoAlt: "Getup AI CRM logo",
    body: "Getup AI CRM helps businesses manage leads, customers, appointments, communication, sales pipelines, and marketing activities from one central platform.",
    capabilities: [
      "Lead and customer relationship management",
      "Sales pipeline and opportunity tracking",
      "Automated email and SMS follow-up",
      "Appointment booking and calendar management",
      "AI-powered website chatbot",
      "AI Voice Agent for incoming and outgoing calls",
      "VoIP business numbers and intelligent call routing",
      "Marketing automation and performance reporting",
    ],
    closing:
      "Getup AI CRM connects customer management, sales, marketing, artificial intelligence, voice communication, and business automation in one integrated platform.",
    cta: "Learn About Getup AI CRM",
    href: GETUP_URL,
    external: true,
  },
  {
    key: "gereal",
    name: "GeReal",
    category: "Real estate operations and property management",
    logo: "/brands/gereal-logo.svg",
    logoAlt: "GeReal real estate software logo",
    body: "GeReal helps real estate agencies, property managers, sales agents, and property professionals manage properties, customers, enquiries, communication, documents, and daily workflows.",
    capabilities: [
      "Property listing and information management",
      "Buyer, seller, tenant, and landlord records",
      "Lead and property enquiry management",
      "Sales and leasing pipeline tracking",
      "Property inspection and open-home scheduling",
      "Offer, application, and document management",
      "Maintenance requests and contractor coordination",
      "Automated email, SMS, task, and follow-up workflows",
    ],
    closing:
      "GeReal brings property information, customer records, communication, documents, and operational workflows together in one connected real estate management platform.",
    cta: "Learn About GeReal",
    href: GETUP_URL,
    external: true,
  },
  {
    key: "getcare-360",
    name: "GetCare 360",
    category: "AI-powered NDIS and care management",
    logo: "/brands/getcare-360-logo.svg",
    logoAlt: "GetCare 360 NDIS software logo",
    body: "GetCare 360 is designed for NDIS providers, aged-care organisations, nursing agencies, supported accommodation services, and community-care businesses.",
    capabilities: [
      "Participant and client profile management",
      "Care plans, goals, and outcome tracking",
      "Progress notes and document management",
      "Incident, risk, and compliance management",
      "Staff rostering and workforce management",
      "Medication and eMAR workflows",
      "Funding, NDIS invoicing, and claims management",
      "Mobile access, reporting, and AI documentation",
    ],
    closing:
      "GetCare 360 helps care providers manage participants, staff, service delivery, compliance, funding, documentation, and reporting through one secure digital platform.",
    cta: "Learn About GetCare 360",
    href: GETUP_URL,
    external: true,
  },
  {
    key: "gettickets",
    name: "GetTickets",
    category: "Modern event ticketing and booking",
    logo: "/brands/gettickets-logo.svg",
    logoAlt: "GetTickets event ticketing platform logo",
    body: "GetTickets helps event organisers create, promote, manage, and deliver professional ticketed events through an easy-to-use online platform.",
    capabilities: [
      "Online event creation and publishing",
      "General-admission and reserved-seat ticketing",
      "Interactive venue and seating-map management",
      "Secure online payments and service-fee handling",
      "Digital tickets with unique QR codes",
      "Automated ticket confirmation and delivery",
      "Mobile ticket scanning and attendee check-in",
      "Sales, order, attendee, and event reporting",
    ],
    closing:
      "GetTickets provides organisers with the technology required to manage the complete event journey, from event publishing and ticket sales to customer communication, entry management, and reporting.",
    cta: "Explore GetTickets",
    href: "/events", // existing internal route
    external: false,
  },
  {
    key: "m4-tv-australia",
    name: "M4 TV Australia",
    category: "Digital television, media and cultural storytelling",
    logo: "/brands/m4-tv-australia-logo.svg",
    logoAlt: "M4 TV Australia digital media platform logo",
    body: "M4 TV Australia is a digital media and entertainment platform that shares diverse Australian stories while connecting multicultural communities through television, online video, original programming, and live content.",
    capabilities: [
      "Live digital television broadcasting",
      "Online video and program streaming",
      "Original television and media production",
      "Multicultural and multilingual programming",
      "News, interviews, and community stories",
      "Music, entertainment, and cultural programs",
      "Business, education, health, and lifestyle content",
      "Live-event coverage and digital streaming",
    ],
    closing:
      "M4 TV Australia gives communities, organisations, artists, businesses, and creators a professional platform to share meaningful stories with audiences in Australia and around the world.",
    cta: "Visit M4 TV Australia",
    href: "https://www.m4tv.com.au/",
    external: true,
  },
  {
    key: "finmar",
    name: "FINMAR",
    category: "Finance, marketing and AI automation",
    logo: "/brands/finmar-logo.svg",
    logoAlt: "FINMAR business platform logo",
    body: "FINMAR is an integrated business-support platform created for Australian small and medium-sized businesses. It brings accounting, digital marketing, artificial intelligence, and business automation together through one connected service ecosystem.",
    capabilities: [
      "Accounting and monthly bookkeeping support",
      "BAS and GST reporting assistance",
      "Payroll processing and administration",
      "Financial statements and business reporting",
      "Social media and content management",
      "Search engine and paid advertising support",
      "AI dashboards and document automation",
      "CRM, marketing, and workflow automation",
    ],
    closing:
      "FINMAR helps business owners reduce administrative complexity by bringing essential financial, marketing, and automation services together through one coordinated platform.",
    cta: "Explore FINMAR",
    href: "https://finmar.com.au/",
    external: true,
  },
];

const WHY_DIFFERENT = [
  { icon: MapPin, label: "Australian-based technology and support" },
  { icon: Code2, label: "Platform developed by an experienced software team" },
  { icon: Layers, label: "General-admission and reserved-seating options" },
  { icon: ShieldCheck, label: "Secure online payments and digital ticket delivery" },
  { icon: ScanLine, label: "Mobile ticket scanning and attendee management" },
  { icon: Users, label: "Flexible organiser and staff access" },
  { icon: Sparkles, label: "Continuous product improvements" },
  { icon: Building2, label: "Custom development and integration capabilities" },
  { icon: Megaphone, label: "Website, branding, marketing, and promotional support" },
];

const APPROACH = [
  { n: "01", title: "Understand", body: "We begin by understanding the organisation, its customers, operational requirements, challenges, and objectives." },
  { n: "02", title: "Design", body: "We create practical and professional user experiences that are simple to use and aligned with the organisation’s brand." },
  { n: "03", title: "Build", body: "Our development team creates secure and scalable software using modern technologies and structured development practices." },
  { n: "04", title: "Launch", body: "We support system configuration, testing, training, deployment, and launch." },
  { n: "05", title: "Support and Improve", body: "After launch, we continue to maintain, support, optimise, and expand the platform as requirements evolve." },
];

const AU_POINTS = [
  "Security and privacy",
  "Role-based access",
  "Reliable data management",
  "Operational transparency",
  "Mobile-friendly user experiences",
  "Scalable software architecture",
  "Practical business automation",
  "Long-term product development",
];

/* ---------- Sub-components ---------- */

function ServiceRow({ icon: Icon, title, body }) {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <li className="flex items-start gap-3" data-testid={`www-service-${slug}`}>
      <span
        className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-md bg-[var(--gt-accent)]/10 text-[var(--gt-accent)]"
        aria-hidden="true"
      >
        <Icon size={18} strokeWidth={1.8} />
      </span>
      <div>
        <h3 className="font-heading text-base font-bold tracking-tight leading-snug">{title}</h3>
        <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function ProductCard({ product }) {
  const { key, name, category, logo, logoAlt, body, capabilities, closing, cta, href, external } = product;
  const btnClasses =
    "mt-6 inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-sm border border-[var(--gt-ink)] transition-colors";
  return (
    <article
      className="gt-card h-full flex flex-col"
      data-testid={`www-product-card-${key}`}
      aria-labelledby={`prod-${key}-name`}
    >
      <div className="flex items-center justify-center h-20 md:h-20 mb-5" aria-hidden="false">
        <img
          src={logo}
          alt={logoAlt}
          loading="lazy"
          className="max-h-[60px] md:max-h-[80px] w-auto object-contain"
          data-testid={`www-product-logo-${key}`}
        />
      </div>
      <h3
        id={`prod-${key}-name`}
        className="font-heading text-2xl font-black tracking-tight text-center"
      >
        {name}
      </h3>
      <p className="text-xs uppercase tracking-widest text-[var(--gt-ink-3)] mt-1 mb-4 text-center">
        {category}
      </p>
      <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed">{body}</p>
      <div className="mt-5 mb-4">
        <div className="gt-label mb-2">Key capabilities</div>
        <ul className="space-y-2" data-testid={`www-product-caps-${key}`}>
          {capabilities.map((c) => (
            <li key={c} className="flex items-start gap-2 text-sm text-[var(--gt-ink-2)]">
              <Check size={14} className="mt-1 shrink-0 text-[var(--gt-accent)]" aria-hidden="true" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed">{closing}</p>
      <div className="mt-auto pt-4">
        {href ? (
          external ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${btnClasses} bg-[var(--gt-ink)] text-white hover:bg-[var(--gt-accent)] hover:border-[var(--gt-accent)]`}
              data-testid={`www-product-cta-${key}`}
              aria-label={`${cta} (opens in a new tab)`}
            >
              {cta} <ArrowUpRight size={16} aria-hidden="true" />
            </a>
          ) : (
            <Link
              to={href}
              className={`${btnClasses} bg-[var(--gt-ink)] text-white hover:bg-[var(--gt-accent)] hover:border-[var(--gt-accent)]`}
              data-testid={`www-product-cta-${key}`}
            >
              {cta} <ArrowRight size={16} aria-hidden="true" />
            </Link>
          )
        ) : (
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Product website coming soon"
            className={`${btnClasses} bg-white text-[var(--gt-ink-3)] cursor-not-allowed opacity-70`}
            data-testid={`www-product-cta-${key}`}
          >
            {cta} · Coming soon
          </button>
        )}
      </div>
    </article>
  );
}

/* ---------- Page ---------- */

export default function WhoWeAre() {
  const canonical = typeof window !== "undefined" ? window.location.origin + "/who-we-are" : undefined;

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: CONTACT.legalName,
      url: CONTACT.website,
      email: CONTACT.email,
      telephone: [CONTACT.tollFree, CONTACT.mobile],
      address: {
        "@type": "PostalAddress",
        streetAddress: CONTACT.addressLines[0],
        addressLocality: "Docklands",
        addressRegion: "VIC",
        postalCode: "3008",
        addressCountry: "AU",
      },
      makesOffer: PRODUCTS.map((p) => ({
        "@type": "Offer",
        itemOffered: { "@type": "SoftwareApplication", name: p.name, description: p.category },
      })),
    }),
    []
  );

  useDocumentMeta({
    title: "Who We Are | GetTickets and Getup Solutions",
    description:
      "Learn about GetTickets and Getup Solutions, the Australian company behind Getup AI CRM, GeReal, GetCare 360, M4 TV Australia, FINMAR, and the GetTickets event ticketing platform.",
    url: canonical,
    type: "website",
    jsonLd,
  });

  const scrollToPortfolio = (e) => {
    e.preventDefault();
    document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="min-h-screen bg-[var(--gt-bg-marketing)] text-[var(--gt-ink)]"
      data-testid="who-we-are-page"
    >
      {/* Nav */}
      <nav className="gt-glass-nav sticky top-0 z-40" aria-label="Primary">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/events"
              data-testid="nav-browse-events-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Browse events
            </Link>
            <Link
              to="/pricing"
              data-testid="nav-pricing-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Pricing
            </Link>
            <Link
              to="/who-we-are"
              data-testid="nav-who-we-are-link"
              className="gt-btn-ghost text-sm"
              aria-current="page"
            >
              Who We Are
            </Link>
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              data-testid="nav-signup-btn"
              className="gt-btn-primary text-sm py-2"
            >
              Sign up
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="/brands/getup-solutions-logo.svg"
                alt="Getup Solutions logo"
                loading="eager"
                className="h-10 w-auto object-contain"
                data-testid="www-hero-logo"
              />
              <span className="gt-label" data-testid="www-hero-eyebrow">
                Powered by Getup Solutions
              </span>
            </div>
            <h1
              className="font-heading font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-6"
              data-testid="www-h1"
            >
              Who We Are<span className="text-[var(--gt-accent)]">.</span>
            </h1>
            <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed max-w-3xl mb-4">
              GetTickets is proudly designed, developed, and powered by <strong>Getup Solutions Pty Ltd</strong>, an
              Australian software development, AI automation, and digital transformation company.
            </p>
            <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed max-w-3xl mb-4">
              Founded in 2013, Getup Solutions helps businesses and organisations improve how they operate,
              communicate, serve customers, and grow through intelligent digital technology.
            </p>
            <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed max-w-3xl mb-4">
              We design and develop custom software, web applications, mobile apps, CRM platforms, artificial
              intelligence solutions, workflow automation systems, websites, e-commerce platforms, and digital
              marketing solutions.
            </p>
            <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed max-w-3xl mb-8">
              Our goal is to make advanced technology practical, accessible, and valuable for organisations of every
              size.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={GETUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gt-btn-primary"
                data-testid="www-hero-primary-btn"
                aria-label="Visit Getup Solutions (opens in a new tab)"
              >
                Visit Getup Solutions <ArrowUpRight size={16} aria-hidden="true" />
              </a>
              <a
                href="#portfolio"
                onClick={scrollToPortfolio}
                className="gt-btn-secondary"
                data-testid="www-hero-secondary-btn"
              >
                Explore our portfolio
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* What we do */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16" aria-labelledby="what-we-do-heading">
        <div className="mb-8">
          <div className="gt-label mb-3">Our services</div>
          <h2 id="what-we-do-heading" className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none">
            What We Do
          </h2>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10"
          data-testid="www-services-grid"
        >
          {SERVICE_CATEGORIES.map((cat) => (
            <div key={cat.key} data-testid={`www-service-category-${cat.key}`}>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--gt-ink-3)] mb-5">
                {cat.label}
              </div>
              <ul className="space-y-5">
                {cat.items.map((s) => (
                  <ServiceRow key={s.title} {...s} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Portfolio */}
      <section
        id="portfolio"
        className="max-w-7xl mx-auto px-6 sm:px-10 pb-16 scroll-mt-24"
        aria-labelledby="portfolio-heading"
      >
        <div className="mb-8">
          <div className="gt-label mb-3">Portfolio</div>
          <h2
            id="portfolio-heading"
            className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none"
          >
            Our Products and Platforms
          </h2>
          <p className="mt-3 text-[var(--gt-ink-2)] max-w-3xl">
            Getup Solutions has developed a growing portfolio of specialised software, business, media, and digital
            platforms. Each product has been created to address specific industry requirements while improving
            operations, communication, automation, customer service, and digital engagement.
          </p>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch"
          data-testid="www-products-grid"
        >
          {PRODUCTS.map((p) => (
            <ProductCard key={p.key} product={p} />
          ))}
        </div>
      </section>

      {/* Why GetTickets is different */}
      <section
        className="max-w-7xl mx-auto px-6 sm:px-10 pb-16"
        aria-labelledby="why-different-heading"
      >
        <div className="mb-8">
          <div className="gt-label mb-3">The GetTickets edge</div>
          <h2
            id="why-different-heading"
            className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none"
          >
            Why GetTickets Is Different
          </h2>
          <p className="mt-3 text-[var(--gt-ink-2)] max-w-3xl">
            GetTickets is more than a ticket-selling website. It is backed by an Australian software development and
            digital transformation company with experience across technology, automation, media, marketing, and event
            management.
          </p>
        </div>
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="www-why-different-list"
        >
          {WHY_DIFFERENT.map(({ icon: Icon, label }) => (
            <li
              key={label}
              className="flex items-start gap-3 border border-[var(--gt-line)] rounded-sm p-4 bg-white"
            >
              <Icon size={20} strokeWidth={1.6} className="text-[var(--gt-accent)] shrink-0 mt-0.5" aria-hidden="true" />
              <span className="text-sm text-[var(--gt-ink-2)]">{label}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-[var(--gt-ink-2)] max-w-3xl leading-relaxed">
          Our experience across software development, artificial intelligence, digital marketing, creative production,
          media, and event management allows us to support the complete event experience.
        </p>
      </section>

      {/* Our approach */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16" aria-labelledby="approach-heading">
        <div className="mb-8">
          <div className="gt-label mb-3">How we work</div>
          <h2 id="approach-heading" className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none">
            Our Approach
          </h2>
        </div>
        <ol
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
          data-testid="www-approach-list"
        >
          {APPROACH.map(({ n, title, body }) => (
            <li key={n} className="gt-card h-full">
              <div className="text-xs font-mono tracking-widest uppercase text-[var(--gt-ink-3)] mb-2">
                Step {n}
              </div>
              <h3 className="font-heading text-lg font-bold tracking-tight mb-2">{title}</h3>
              <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Built for Australian organisations */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16" aria-labelledby="au-heading">
        <div className="mb-8">
          <div className="gt-label mb-3">Local by design</div>
          <h2 id="au-heading" className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none">
            Built for Australian Organisations
          </h2>
          <p className="mt-3 text-[var(--gt-ink-2)] max-w-3xl">
            GetTickets and the wider Getup Solutions product portfolio are designed with Australian businesses, service
            providers, community organisations, and event organisers in mind.
          </p>
        </div>
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
          data-testid="www-au-list"
        >
          {AU_POINTS.map((p) => (
            <li
              key={p}
              className="flex items-start gap-2 border border-[var(--gt-line)] rounded-sm p-4 bg-white text-sm text-[var(--gt-ink-2)]"
            >
              <Check size={16} className="text-[var(--gt-accent)] mt-0.5 shrink-0" aria-hidden="true" />
              {p}
            </li>
          ))}
        </ul>
      </section>

      {/* Our vision */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 pb-16 text-center" aria-labelledby="vision-heading">
        <div className="gt-label mb-3">Our vision</div>
        <h2 id="vision-heading" className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-tight mb-5">
          Building intelligent platforms for growth.
        </h2>
        <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed mb-4">
          Our vision is to build intelligent digital platforms that give organisations greater control over their
          operations, customer experiences, communication, content, and future growth.
        </p>
        <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed">
          Through GetTickets and our wider portfolio of technology and media platforms, we are developing solutions
          that connect people, support businesses, strengthen communities, and make complex processes easier.
        </p>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16" aria-labelledby="final-cta-heading">
        <div
          className="relative overflow-hidden bg-[var(--gt-ink)] text-white rounded-sm p-8 sm:p-12"
          data-testid="www-final-cta"
        >
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-white/60 mb-4">Let’s work together</div>
            <h2
              id="final-cta-heading"
              className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1] mb-4"
            >
              Let’s Build Something Meaningful<span className="text-[var(--gt-accent)]">.</span>
            </h2>
            <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
              Whether you need to organise an event, automate your business, introduce an intelligent CRM, build custom
              software, develop a mobile application, improve your digital marketing, or create professional media
              content, Getup Solutions is ready to help.
            </p>
            <div className="text-sm text-white/60 mb-8">
              <div className="font-heading text-white text-lg">GetTickets</div>
              Designed, developed, and powered by Getup Solutions.
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={GETUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[var(--gt-accent)] text-white font-semibold text-sm px-6 py-3 rounded-sm hover:opacity-90 transition-opacity"
                data-testid="www-cta-getup-btn"
                aria-label="Visit Getup Solutions (opens in a new tab)"
              >
                Visit Getup Solutions <ArrowUpRight size={16} aria-hidden="true" />
              </a>
              <Link
                to="/events"
                className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold text-sm px-6 py-3 rounded-sm hover:border-white transition-colors"
                data-testid="www-cta-events-btn"
              >
                Explore Events
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24" aria-labelledby="contact-heading">
        <div className="mb-6">
          <div className="gt-label mb-3">Contact</div>
          <h2 id="contact-heading" className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none">
            Getup Solutions Pty Ltd
          </h2>
          <p className="mt-2 text-xs font-mono tracking-widest uppercase text-[var(--gt-ink-3)]">
            ABN {CONTACT.abn}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="www-contact-grid">
          <div className="gt-card h-full">
            <MapPin size={22} strokeWidth={1.6} className="text-[var(--gt-accent)] mb-3" aria-hidden="true" />
            <div className="gt-label mb-2">Address</div>
            <address className="not-italic text-sm text-[var(--gt-ink-2)] leading-relaxed">
              {CONTACT.addressLines.map((l) => (
                <div key={l}>{l}</div>
              ))}
            </address>
          </div>
          <a href={`mailto:${CONTACT.email}`} className="gt-card h-full block hover:border-[var(--gt-accent)] transition-colors" data-testid="www-contact-email">
            <Mail size={22} strokeWidth={1.6} className="text-[var(--gt-accent)] mb-3" aria-hidden="true" />
            <div className="gt-label mb-2">Email</div>
            <span className="text-sm text-[var(--gt-ink-2)] leading-relaxed break-words">{CONTACT.email}</span>
          </a>
          <a href={`tel:${CONTACT.tollFree.replace(/\s/g, "")}`} className="gt-card h-full block hover:border-[var(--gt-accent)] transition-colors" data-testid="www-contact-tollfree">
            <PhoneIcon size={22} strokeWidth={1.6} className="text-[var(--gt-accent)] mb-3" aria-hidden="true" />
            <div className="gt-label mb-2">Toll free</div>
            <span className="text-sm text-[var(--gt-ink-2)] leading-relaxed">{CONTACT.tollFree}</span>
            <div className="mt-3 gt-label">Mobile</div>
            <a
              href={`tel:${CONTACT.mobile.replace(/\s/g, "")}`}
              className="text-sm text-[var(--gt-ink-2)] hover:text-[var(--gt-accent)]"
              data-testid="www-contact-mobile"
            >
              {CONTACT.mobile}
            </a>
          </a>
          <a
            href={GETUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="gt-card h-full block hover:border-[var(--gt-accent)] transition-colors"
            data-testid="www-contact-website"
            aria-label="getupsolutions.com.au (opens in a new tab)"
          >
            <ExternalLink size={22} strokeWidth={1.6} className="text-[var(--gt-accent)] mb-3" aria-hidden="true" />
            <div className="gt-label mb-2">Website</div>
            <span className="text-sm text-[var(--gt-ink-2)] leading-relaxed break-words">www.getupsolutions.com.au</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 text-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-[var(--gt-ink-3)] text-xs tracking-widest uppercase" data-testid="footer-copyright">
            © {new Date().getFullYear()} Getickets · Made for Aussie organisers. Powered by{" "}
            <a
              href="https://getupsolutions.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--gt-accent)]"
              data-testid="footer-powered-by-link"
            >
              www.getupsolutions.com.au
            </a>
          </div>
          <div className="flex gap-4 text-xs flex-wrap">
            <Link to="/" className="hover:text-[var(--gt-accent)]" data-testid="footer-home-link">Home</Link>
            <Link to="/events" className="hover:text-[var(--gt-accent)]" data-testid="footer-events-link">Browse events</Link>
            <Link to="/who-we-are" className="hover:text-[var(--gt-accent)]" data-testid="footer-who-we-are-link">Who we are</Link>
            <Link to="/privacy" className="hover:text-[var(--gt-accent)]" data-testid="footer-privacy-link">Privacy</Link>
            <ManageCookiesLink className="text-xs" />
            <a className="hover:text-[var(--gt-accent)]" href="mailto:admin@getickets.com.au">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
